import {
    Autocomplete,
    AutocompleteProps,
    AutocompleteRenderGroupParams,
    Box,
    Chip,
    FormGroup,
    Popper,
    PopperProps,
    TextField,
    Typography,
    debounce,
} from '@mui/material'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress/C1CircularProgress'
import {
    defaultIsEqual,
    defaultRenderLabel,
    defaultRenderOption,
    Searchable,
} from 'components/core/autocomplete'
import { C1AutocompleteStyles } from 'components/core/autocomplete/styles'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

const AutocompletePopper = (props: PopperProps & { 'aria-label'?: string }): ReactElement => (
    <Popper {...props} role="region" />
)

// Accessible renderGroup that fixes ARIA structure when using groupBy
// MUI's default creates invalid ARIA: listbox > li > ul > li[role=option]
// This renders: listbox > li[role=presentation] > div (header) + options directly
const accessibleRenderGroup = (params: AutocompleteRenderGroupParams): React.ReactNode => <li key={params.key} role="presentation">
    <Typography
        variant="h6"
        component="div"
        color="text.secondary"
        sx={{ px: 3, py: 2 }}
    >
        {params.group}
    </Typography>
    <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }} role="group" aria-label={params.group}>
        {params.children}
    </Box>
</li>

export type C1AutocompleteMultipleSelectProps<T extends Searchable> = {
    onQueryChange: (string) => void
    resultList: T[]
    initialValue?: T[]
    onSelected: (v: T[]) => void
    getDisplayName?: (option: T | null) => string
    renderLabel?: (option: T) => string
    isEqual?: (option: T, value: T) => boolean
    error?: boolean
    helperText?: string
    autoFocus?: boolean
    placeholder?: string
    label?: string
} & Partial<AutocompleteProps<T, true, undefined, undefined>>

export const C1AutocompleteMultipleSelect = <T extends Searchable>({
    onQueryChange,
    resultList,
    initialValue,
    onSelected,
    isEqual = defaultIsEqual,
    renderLabel = defaultRenderLabel,
    renderOption = defaultRenderOption,
    error,
    helperText,
    autoFocus = false,
    placeholder,
    label,
    loading,
    ...rest
}: C1AutocompleteMultipleSelectProps<T>): React.ReactElement => {
    const intl = useIntl()
    const [values, setValues] = useState<T[]>([])
    const [inputValue, setInputValue] = useState<string | null>(null)
    const [options, setOptions] = useState<T[]>([])

    const onQueryChangeDebounced = React.useMemo(
        () =>
            debounce((query: string) => {
                onQueryChange(query)
            }, 400),
        [onQueryChange],
    )

    const filterOptions = useCallback(
        (existingList: T[], newList: T[]): T[] => {
            return [...existingList.filter((v) => !newList.some((r) => isEqual(v, r))), ...newList]
        },
        [isEqual],
    )

    useEffect(() => {
        setOptions(filterOptions(values, resultList))
    }, [filterOptions, resultList, values])

    useEffect(() => {
        if (initialValue && initialValue.length > 0) {
            setOptions(filterOptions(options, initialValue))
            setValues(initialValue)
        }
    }, [initialValue]) // eslint-disable-line react-hooks/exhaustive-deps

    // Reset this components internal state when the parent component resets the value
    useEffect(() => {
        if (rest.value?.length === 0) setValues([])
    }, [rest.value])

    // Use accessible renderGroup when groupBy is provided to fix ARIA structure
    const groupByRenderGroup = rest.groupBy ? accessibleRenderGroup : undefined

    return (
        <Autocomplete<T, true, undefined, undefined>
            multiple
            fullWidth
            filterOptions={(x): T[] => x}
            clearIcon={false}
            filterSelectedOptions
            value={values}
            options={options}
            getOptionLabel={renderLabel}
            isOptionEqualToValue={isEqual}
            renderOption={renderOption}
            renderGroup={groupByRenderGroup}
            renderInput={(params): ReactElement => {
                return (<FormGroup>
                    {label && <C1FormLabel label={label} htmlFor="autocomplete" />}
                    <TextField
                        name="autocomplete"
                        {...params}
                        sx={C1AutocompleteStyles.textField}
                        autoFocus={autoFocus}
                        fullWidth
                        error={error}
                        helperText={helperText}
                        placeholder={placeholder}
                        size="small"
                        InputProps={{
                            ...params.InputProps,
                            type: 'autocomplete',
                            endAdornment: <>
                                {loading && <C1CircularProgress color="inherit" />}
                                {params.InputProps.endAdornment}
                            </>,
                        }}
                    />
                </FormGroup>)
            }}
            renderTags={(value, getTagProps): React.ReactElement[] => {
                return value.map((option, index) => (
                    <Chip color="primary" label={option.displayName} data-dd-action-name="Remove selection" {...getTagProps({ index })} />
                ))
            }}
            onBlur={(): void => {
                if (inputValue && inputValue.length > 0) {
                    // Clear out filtered results as we will get full results on next focus
                    setOptions([])
                }
                onQueryChangeDebounced.clear()
            }}
            onFocus={(): void => {
                if (inputValue === null || inputValue.length > 0) {
                    // Get full list only if first time load (null) or if previously filtered(len > 0)
                    // We've already loaded the full list otherwise
                    onQueryChange('')
                }
                // Optimization to clear out just the input value, so we won't fetch full list again
                // The autocomplete value still stays the same, and UI still shows the previously selected value
                setInputValue('')
            }}
            onInputChange={(event, newInputValue): void => {
                setInputValue(newInputValue)
                onQueryChangeDebounced(newInputValue)
            }}
            onChange={(e, val): void => {
                const vals = val || []
                setValues(vals)
                onSelected(vals)
            }}
            PopperComponent={(props): ReactElement => (
                <AutocompletePopper
                    {...props}
                    aria-label={intl.formatMessage({ id: 'autocomplete_search_results', defaultMessage: 'Search results' })}
                />
            )}
            {...rest}
        />
    )
}
